import { PASSTHROUGH_KEYS, TW_REFERRER_KEY } from './constants'
import {
  deleteParamCaseInsensitive,
  getParamCaseInsensitive,
  hasParamCaseInsensitive,
  isPersistedParamKey,
} from './normalize'
import {
  extractMarketingParams,
  extractTwReferrerFromSearch,
  serializeMarketingParams,
} from './parse'
import { getPersistedParams } from './store'

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

function isThriveworksHostname(hostname: string): boolean {
  return hostname === 'thriveworks.com' || hostname.endsWith('.thriveworks.com')
}

/**
 * Whether persisted marketing params should be appended to a target URL.
 * Applies to relative paths and thriveworks.com domains; skips tel: and third-party URLs.
 */
export function shouldApplyMarketingParams(url: string): boolean {
  if (url.startsWith('tel:')) {
    return false
  }

  // Protocol-relative URLs are absolute URLs, not app-relative paths.
  if (url.startsWith('//')) {
    try {
      const { hostname } = new URL(`https:${url}`)

      return isThriveworksHostname(hostname)
    } catch {
      return false
    }
  }

  if (url.startsWith('/')) {
    return true
  }

  try {
    const { hostname } = new URL(url)

    return isThriveworksHostname(hostname)
  } catch {
    return false
  }
}

/**
 * Append the persisted marketing params (+ tw_referrer) to a target URL,
 * preserving any non-marketing params already present. Existing values for
 * persisted keys are overwritten with the persisted snapshot. `tw_referrer` is
 * only added if not already present on the target.
 *
 * Accepts absolute or relative URLs and returns the same shape.
 */
export function buildUrlWithMarketingParams(target: string): string {
  if (!isBrowser()) {
    return target
  }

  const persisted = getPersistedParams()

  if (Object.keys(persisted).length === 0) {
    return target
  }

  const base = window.location.origin
  const isAbsolute = /^(https?:)?\/\//i.test(target)
  const url = new URL(target, base)

  Object.entries(persisted).forEach(([key, value]) => {
    if (!value) {
      return
    }

    // Don't overwrite an existing tw_referrer already on the target URL.
    if (
      key === TW_REFERRER_KEY &&
      hasParamCaseInsensitive(url.searchParams, TW_REFERRER_KEY)
    ) {
      return
    }

    // Don't overwrite a passthrough key already present on the target URL.
    // This prevents a stale persisted sfId/sfObjectType/partnershipCode from
    // replacing the correct value the caller already set on the link.
    if (
      PASSTHROUGH_KEYS.includes(key as typeof PASSTHROUGH_KEYS[number]) &&
      hasParamCaseInsensitive(url.searchParams, key)
    ) {
      return
    }

    deleteParamCaseInsensitive(url.searchParams, key)
    url.searchParams.set(key, value)
  })

  return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`
}

/**
 * Convenience wrapper for hard-navigation/history call sites that need to carry
 * persisted marketing params forward on a target URL.
 */
export function withMarketingParams(target: string): string {
  return buildUrlWithMarketingParams(target)
}

/**
 * Appends persisted marketing params when the target URL is eligible; otherwise
 * returns the URL unchanged.
 */
export function enhanceUrlWithMarketingParams(url: string): string {
  return shouldApplyMarketingParams(url) ? withMarketingParams(url) : url
}

/**
 * SSR-safe: append allowlisted marketing params from an incoming query string
 * to a relative target path. Returns the path unchanged when no marketing
 * params are present.
 */
export function appendMarketingParamsFromSearch(
  targetPath: string,
  search: string
): string {
  const marketing = extractMarketingParams(search)
  const twReferrer = extractTwReferrerFromSearch(search)
  const serialized = serializeMarketingParams(marketing)

  if (!serialized && !twReferrer) {
    return targetPath
  }

  const [pathname, existingQuery = ''] = targetPath.split('?')
  const params = new URLSearchParams(existingQuery)

  if (serialized) {
    const marketingParams = new URLSearchParams(serialized)

    marketingParams.forEach((value, key) => {
      deleteParamCaseInsensitive(params, key)
      params.set(key, value)
    })
  }

  if (twReferrer) {
    deleteParamCaseInsensitive(params, TW_REFERRER_KEY)
    params.set(TW_REFERRER_KEY, twReferrer)
  }

  const query = params.toString()

  return query ? `${pathname}?${query}` : pathname
}

/**
 * Re-append persisted marketing params to the CURRENT URL via
 * history.replaceState when they're missing (or stale). No-op when there are no
 * persisted params or the URL already matches. Never triggers a navigation or
 * scroll.
 */
export function reapplyMarketingParamsToUrl(): void {
  if (!isBrowser()) {
    return
  }

  const persisted = getPersistedParams()

  if (Object.keys(persisted).length === 0) {
    return
  }

  const url = new URL(window.location.href)
  let changed = false

  Object.entries(persisted).forEach(([key, value]) => {
    if (!value) {
      return
    }

    // Don't overwrite an existing tw_referrer already on the current URL.
    if (
      key === TW_REFERRER_KEY &&
      hasParamCaseInsensitive(url.searchParams, TW_REFERRER_KEY)
    ) {
      return
    }

    // Don't overwrite a passthrough key already on the current URL.
    if (
      PASSTHROUGH_KEYS.includes(key as typeof PASSTHROUGH_KEYS[number]) &&
      hasParamCaseInsensitive(url.searchParams, key)
    ) {
      return
    }

    if (getParamCaseInsensitive(url.searchParams, key) !== value) {
      deleteParamCaseInsensitive(url.searchParams, key)
      url.searchParams.set(key, value)
      changed = true
    } else if (
      getParamCaseInsensitive(url.searchParams, key) !== null &&
      url.searchParams.get(key) !== value
    ) {
      deleteParamCaseInsensitive(url.searchParams, key)
      url.searchParams.set(key, value)
      changed = true
    }
  })

  if (changed) {
    const newRelative = `${url.pathname}${url.search}${url.hash}`
    window.history.replaceState(window.history.state, '', newRelative)
  }
}

/**
 * Remove only the persisted marketing params (+ tw_referrer) from the current
 * URL via history.replaceState, leaving all other params intact.
 */
export function stripMarketingParamsFromUrl(): void {
  if (!isBrowser()) {
    return
  }

  const url = new URL(window.location.href)
  let changed = false

  const keysToDelete: string[] = []

  url.searchParams.forEach((_, key) => {
    if (isPersistedParamKey(key)) {
      keysToDelete.push(key)
    }
  })

  keysToDelete.forEach((key) => {
    url.searchParams.delete(key)
    changed = true
  })

  if (changed) {
    const newRelative = `${url.pathname}${url.search}${url.hash}`
    window.history.replaceState(window.history.state, '', newRelative)
  }
}
